/**
 * Adapter que substitui a API de scroll do `<ion-content>` sobre um `<div>` nativo
 * (o `#scrollContainer` que troca o ion-content na desionização). Anima com
 * requestAnimationFrame + easeInOutQuad porque `scrollTo({behavior:'smooth'})` falha
 * quando o container usa `-webkit-overflow-scrolling: touch`.
 *
 * Uso na página:
 *   @ViewChild('scrollContainer') scrollContainerRef!: ElementRef<HTMLElement>;
 *   get content(): ScrollContentAdapter {
 *     return createScrollContentAdapter(() => this.scrollContainerRef?.nativeElement);
 *   }
 * e no template: `<div #scrollContainer class="mp-content"> ... </div>`.
 */
export interface ScrollContentAdapter {
	getScrollElement(): Promise<HTMLElement | undefined>;
	scrollToTop(duration?: number): Promise<void>;
	scrollToBottom(duration?: number): Promise<void>;
	scrollToPoint(x: number, y: number, duration?: number): Promise<void>;
}

export function createScrollContentAdapter(
	getEl: () => HTMLElement | undefined | null,
): ScrollContentAdapter {
	const animate = (to: number, duration?: number): Promise<void> => {
		const el = getEl();
		if (!el) return Promise.resolve();
		if (!duration) {
			el.scrollTop = to;
			return Promise.resolve();
		}
		return new Promise<void>(resolve => {
			const start = el.scrollTop;
			const change = to - start;
			const t0 = performance.now();
			const step = (now: number) => {
				const t = Math.min(1, (now - t0) / duration);
				const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // easeInOutQuad
				el.scrollTop = start + change * eased;
				if (t < 1) requestAnimationFrame(step);
				else resolve();
			};
			requestAnimationFrame(step);
		});
	};

	return {
		getScrollElement: () => Promise.resolve(getEl() ?? undefined),
		scrollToTop: (duration?: number) => animate(0, duration),
		scrollToBottom: (duration?: number) => {
			const el = getEl();
			return animate(el ? el.scrollHeight : 0, duration);
		},
		scrollToPoint: (_x: number, y: number, duration?: number) => animate(y, duration),
	};
}
